<script setup lang="ts">
import { computed, onBeforeMount, ref, Ref, toRefs } from "vue";
import ManageFileDialog from "@/ui/components/FileBrowser/ManageFileDialog.vue";
import { v4 as uuidv4 } from "uuid";
import { useExplorerStore } from "@/modules/explorer/store";
import { useApiClient } from "@/api/client";
import { toastApiResult } from "@/toaster";
import FileDTO = App.Data.FileDTO;
import StoredDocumentDO = App.Data.StoredDocumentDO;
import { convertDates } from "chartjs-graph";
import { useFileFinder } from "@/ui/components/FileBrowser/useFileFinder";
import { FileBrowserAction } from "@/ui/components/FileBrowser/types";
import ResourceTreeNodeDO = App.Data.ResourceTreeNodeDO;
import { useResourceShare } from "@/ui/components/FileBrowser/useResourceShare";
import PublicUserDO = App.Data.PublicUserDO;
import RoleDO = App.Data.RoleDO;
import AbilityName = App.Enums.AbilityName;
import { promptInvalidPermissions } from "@/api/helpers/loadingWrapper";
import { useDashboardRedirect } from "@/modules/explorer/composables/useDashboardRedirect";
import FileExplorer from "@/ui/components/FileBrowser/FileExplorer.vue";

const { redirectBackToDashboard } = useDashboardRedirect();

const props = defineProps<{
	isSaveState: boolean;
	quickCreate?: boolean;
}>();

const { isSaveState } = toRefs(props);

const resourceTreeCategory = "requested_documents";
const isDialogVisible = defineModel<boolean>();

// During a dashboard round-trip the user only needs to name the new
// configuration and pick a folder, so skip the file browser and go
// straight to the create-file dialog.
const isQuickCreate = computed(() => props.quickCreate && isSaveState.value);
const folders: Ref<ResourceTreeNodeDO[]> = ref([]);
const quickCreateFile: Ref<FileDTO | null> = ref(null);

onBeforeMount(async () => {
	if (!isQuickCreate.value) return;

	const response = await useApiClient("resourceTreeNodes").getResourceTreeNodes(
		resourceTreeCategory
	);
	if (response.isOk()) {
		folders.value = response.value;
		quickCreateFile.value = {
			uuid: uuidv4(),
			resource_tree_node_id:
				folders.value.find((folder) => folder.name === "Root")?.id ?? null,
			title: "",
			description: "",
			times_opened: 0,
			is_favorite: false,
			last_opened_at: null,
			last_modified_at: null,
			created_at: null,
		};
	}
});

const removeStoredDocumentFile = async (file: FileDTO) => {
	return await toastApiResult(
		useApiClient("documents").removeStoredDocument(file)
	);
};

const batchRemoveStoredDocumentFiles = async (files: FileDTO[]) => {
	return await toastApiResult(
		useApiClient("documents").batchRemoveStoredDocuments(files)
	);
};

const createStoredDocumentFile = async (file: FileDTO) => {
	const response = await toastApiResult(
		useApiClient("documents").createStoredDocument(
			file as StoredDocumentDO,
			useExplorerStore().document.uuid
		)
	);

	if (response.isOk()) {
		// Also update the document uuid to prevent triggering unsaved changes
		useExplorerStore().document.uuid = response.value.active_document_uuid;
		useExplorerStore().explorerState.lastOpenedSavedDocumentDocumentUuid =
			response.value.active_document_uuid;
		isDialogVisible.value = false;

		await redirectBackToDashboard(response.value);
	}

	if (response.isErr()) {
		useExplorerStore().explorerState.lastOpenedSavedDocumentDocumentUuid = null;
	}

	return response;
};

const updateStoredDocumentFileProperties = async (file: FileDTO) => {
	return await toastApiResult(
		promptInvalidPermissions(
			useApiClient("documents").updateStoredDocument(file as StoredDocumentDO)
		)
	);
};

const updateStoredDocumentDocument = async (file: FileDTO) => {
	if (file) {
		const response = await toastApiResult(
			promptInvalidPermissions(
				useApiClient("documents").updateStoredDocumentDocument(
					file as StoredDocumentDO,
					useExplorerStore().document.uuid
				)
			)
		);

		if (response.isOk()) {
			// Also update the document uuid to prevent triggering unsaved changes
			useExplorerStore().document.uuid = response.value.active_document_uuid;
			useExplorerStore().explorerState.lastOpenedSavedDocumentDocumentUuid =
				response.value.active_document_uuid;
		}

		if (response.isErr()) {
			useExplorerStore().explorerState.lastOpenedSavedDocumentDocumentUuid =
				null;
		}
	}
	closeDialog();
};

const openStoredDocument = async (file: FileDTO) => {
	if (file) {
		const response = await promptInvalidPermissions(
			useApiClient("documents").openStoredDocument(
				file,
				useExplorerStore().document
			)
		);
		if (response.isOk()) {
			useExplorerStore().explorerState.documentFetchedFromHistory = true;

			const documentOpened = response.value.replicated_document;

			// When opening a saved doument, we don't want to trigger logic that runs on user change.
			// By setting this property to true, that is prevented from running.
			const processedDocumentOpened = {
				...convertDates(documentOpened),
			};

			useExplorerStore().explorerState.isOpeningSavedDocument = true;
			useExplorerStore().document = processedDocumentOpened;
			useExplorerStore().explorerState.isOpeningSavedDocument = false;

			useExplorerStore().explorerState.userAbilityForCurrentDocument =
				file?.active_user_abilities;

			const canUpdateDocument =
				file?.active_user_abilities.includes("update") ||
				file?.active_user_abilities.includes("*");

			if (canUpdateDocument) {
				// Also update the document uuid to prevent triggering unsaved changes
				useExplorerStore().document.uuid =
					response.value.original_document_uuid;
			}

			useExplorerStore().explorerState.lastOpenedSavedDocumentDocumentUuid =
				canUpdateDocument ? response.value.original_document_uuid : null;

			// const documentHistory = useDocumentHistory();
			// documentHistory.addDocumentToHistory(processedDocumentOpened);
		}
		closeDialog();
	}
};

const listUsersWithSharePermissionsOnStoredDocument = async (
	resource: FileDTO
) => {
	return await useApiClient(
		"documents"
	).getUsersWithPermissionsToStoredDocument(resource);
};

const revokePermissionsForUserOnStoredDocument = async (
	resource: FileDTO,
	user: PublicUserDO
) => {
	return await useApiClient(
		"documents"
	).revokePermissionsToStoredDocumentForUser(resource, user);
};

const grantPermissionsToUserOnStoredDocument = async (
	resource: FileDTO,
	user: PublicUserDO,
	ability: AbilityName
) => {
	return await useApiClient(
		"documents"
	).grantPermissionsToStoredDocumentForUser(resource, user, ability);
};

const listRolesWithSharePermissionsOnStoredDocument = async (
	resource: FileDTO
) => {
	return await useApiClient(
		"documents"
	).getRolesWithPermissionsToStoredDocument(resource);
};

const revokePermissionsForRoleOnStoredDocument = async (
	resource: FileDTO,
	role: RoleDO
) => {
	return await useApiClient(
		"documents"
	).revokePermissionsToStoredDocumentForRole(resource, role);
};

const grantPermissionsToRoleOnStoredDocument = async (
	resource: FileDTO,
	role: RoleDO,
	ability: AbilityName
) => {
	return await useApiClient(
		"documents"
	).grantPermissionsToStoredDocumentForRole(resource, role, ability);
};

const closeDialog = () => {
	isDialogVisible.value = false;
};

const toggleOrSetFavoriteStoredDocument = async (resource: FileDTO) => {
	return await toastApiResult(
		useApiClient("documents").toggleOrSetFavoriteStoredDocument(resource)
	);
};

useFileFinder({
	openFile: openStoredDocument,
	updateFileProperties: updateStoredDocumentFileProperties,
	createFile: createStoredDocumentFile,
	removeFile: removeStoredDocumentFile,
	batchRemoveFiles: batchRemoveStoredDocumentFiles,
	closeAction: closeDialog,
	updateDefinition: updateStoredDocumentDocument,
	toggleIsFavorite: toggleOrSetFavoriteStoredDocument,
});

useResourceShare(
	listUsersWithSharePermissionsOnStoredDocument,
	grantPermissionsToUserOnStoredDocument,
	revokePermissionsForUserOnStoredDocument,
	listRolesWithSharePermissionsOnStoredDocument,
	grantPermissionsToRoleOnStoredDocument,
	revokePermissionsForRoleOnStoredDocument
);

const computedActions = computed<FileBrowserAction[]>(() => {
	const actions: FileBrowserAction[] = [
		"share-file",
		"delete-file",
		"update-file-meta",
		"new-folder",
		"delete-folder",
		"update-folder",
		"toggle-is-favorite",
		"close",
	];

	if (isSaveState.value) {
		actions.push("new-file");
		actions.push("update-file-definition");
	} else {
		actions.push("open-file");
	}

	return actions;
});
</script>

<template>
	<manage-file-dialog
		v-if="isQuickCreate && quickCreateFile"
		v-model="isDialogVisible"
		:nodes="folders"
		:file-to-edit="quickCreateFile"
		:resource-tree-category="resourceTreeCategory"
	/>

	<v-dialog
		v-else-if="!isQuickCreate"
		v-model="isDialogVisible"
		scrim="#000000"
		overlay-color="black"
		max-width="90vw"
		min-height="400"
	>
		<v-card>
			<v-toolbar style="border-radius: 0 !important" color="secondary" flat>
				<template v-slot:prepend>
					<v-icon start class="ml-3 mr-5">mdi-file-chart-outline</v-icon>
					<v-toolbar-title class="pt-1"> Configurations </v-toolbar-title>
				</template>
			</v-toolbar>
			<v-divider />
			<file-explorer
				:resource-tree-category="resourceTreeCategory"
				:actions="computedActions"
			/>
		</v-card>
	</v-dialog>
</template>

<style lang="scss" scoped></style>
